import { useSelector } from 'react-redux';

import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionStatus,
  PartialInquiryTemplateVersion,
} from 'dashboard/models/inquiry-template-version';
import { RootState } from 'dashboard/redux/store';

/**
 * @name `useIsEditableVersion`
 *
 * A hook that returns whether the current inquiry template version being
 * viewed in Flow Editor should be editable or not.
 */
export default () => {
  const currentTemplate = useSelector<RootState, InquiryTemplate | undefined>(
    (state) => state.inquiryTemplate.inquiryTemplate as InquiryTemplate,
  );
  const currentVersion = useSelector<RootState, InquiryTemplateVersion | undefined>(
    (state) => state.inquiryTemplate.inquiryTemplateTreeData?.inquiryTemplateVersion,
  );
  const localRestoredDraftVersion = useSelector<
    RootState,
    PartialInquiryTemplateVersion | undefined
  >((state) => state.inquiryTemplate.localDraftPartialInquiryTemplateVersion);

  if (currentTemplate == null || currentVersion == null) {
    return false;
  }

  if (currentTemplate.attributes['archived-at'] != null) {
    return false;
  }

  // when a version is restored from the versions panel, the currentVersion would
  // still evaluate as immutable (prior published), but editing should be enabled
  if (localRestoredDraftVersion != null) {
    return true;
  }

  const {
    'latest-draft-version': latestDraftVersion,
    'latest-published-version': latestPublishedVersion,
  } = currentTemplate.relationships;

  if (currentVersion.attributes.status === InquiryTemplateVersionStatus.Draft) {
    // only permit editing for a draft if it's the latest draft version
    return currentVersion.id === latestDraftVersion?.data?.id;
  } else {
    // only permit editing if it's the latest published version and no draft exists
    if (currentVersion.id === latestPublishedVersion?.data?.id) {
      return latestDraftVersion?.data?.id == null;
    } else {
      return false;
    }
  }
};
